import { PasseriniAPI } from './apiService';
import { TramiteParametro, TramiteParametroFormData } from '../types';

export const tramiteParametroService = {
  // Listar todos los parámetros de un trámite
  async getAll(tramiteId: number): Promise<TramiteParametro[]> {
    const response = await PasseriniAPI.get(`/tramites/${tramiteId}/parametros`);
    return response.data?.data || [];
  },

  // Crear un nuevo parámetro
  async create(tramiteId: number, data: TramiteParametroFormData): Promise<TramiteParametro> {
    const response = await PasseriniAPI.post(`/tramites/${tramiteId}/parametros`, data);
    return response.data?.data;
  },

  // Obtener un parámetro por ID
  async getById(tramiteId: number, parametroId: number): Promise<TramiteParametro> {
    const response = await PasseriniAPI.get(`/tramites/${tramiteId}/parametros/${parametroId}`);
    return response.data?.data;
  },

  // Obtener un parámetro por nombre
  async getByName(tramiteId: number, nombre: string): Promise<TramiteParametro> {
    try {
      const response = await PasseriniAPI.get(`/tramites/${tramiteId}/parametros/nombre/${nombre}`);
      return response.data?.data;
    } catch (error: any) {
      // Silently handle 404 - parameter doesn't exist yet
      if (error?.response?.status === 404) {
        throw error; // Re-throw to be handled by caller
      }
      throw error;
    }
  },

  // Actualizar un parámetro
  async update(tramiteId: number, parametroId: number, data: TramiteParametroFormData): Promise<TramiteParametro> {
    const response = await PasseriniAPI.put(`/tramites/${tramiteId}/parametros/${parametroId}`, data);
    return response.data?.data;
  },

  // Eliminar un parámetro
  async delete(tramiteId: number, parametroId: number): Promise<void> {
    await PasseriniAPI.delete(`/tramites/${tramiteId}/parametros/${parametroId}`);
  },

  // Crear o actualizar un parámetro por nombre (helper method)
  async createOrUpdate(tramiteId: number, nombre: string, valor: string, tipo: string = 'string'): Promise<TramiteParametro> {
    try {
      // Intentar obtener el parámetro existente
      const existing = await this.getByName(tramiteId, nombre);

      // Si existe y tiene ID, actualizarlo
      if (existing && existing.id) {
        return await this.update(tramiteId, existing.id, { nombre, valor, tipo: tipo as any });
      } else {
        // Si no existe o no tiene ID, crearlo
        return await this.create(tramiteId, { nombre, valor, tipo: tipo as any });
      }
    } catch (error) {
      // Si hay error (probablemente 404), crearlo
      return await this.create(tramiteId, { nombre, valor, tipo: tipo as any });
    }
  },

  // Obtener valor de un parámetro parseado según su tipo
  async getValue(tramiteId: number, nombre: string): Promise<any> {
    try {
      const parametro = await this.getByName(tramiteId, nombre);
      return this.parseValue(parametro.valor, parametro.tipo);
    } catch (error) {
      return null;
    }
  },

  // Obtener valor de un parámetro con valor por defecto si no existe
  async getValueOrDefault(tramiteId: number, nombre: string, defaultValue: any): Promise<any> {
    try {
      const parametro = await this.getByName(tramiteId, nombre);

      if (parametro && parametro.valor !== undefined && parametro.valor !== null) {
        const parsedValue = this.parseValue(parametro.valor, parametro.tipo);
        return parsedValue;
      } else {
        return defaultValue;
      }
    } catch (error) {
      return defaultValue;
    }
  },

  // Helper para parsear valores según el tipo
  parseValue(valor: string, tipo: string): any {
    switch (tipo) {
      case 'number':
        return parseFloat(valor);
      case 'boolean':
        return valor === 'true' || valor === '1';
      case 'date':
        return new Date(valor);
      case 'json':
        try {
          return JSON.parse(valor);
        } catch {
          return valor;
        }
      default:
        return valor;
    }
  },

  // Helper para formatear valores para envío
  formatValue(valor: any, tipo: string): string {
    switch (tipo) {
      case 'number':
        return valor.toString();
      case 'boolean':
        return valor ? 'true' : 'false';
      case 'date':
        return valor instanceof Date ? valor.toISOString() : valor;
      case 'json':
        return typeof valor === 'string' ? valor : JSON.stringify(valor);
      default:
        return valor.toString();
    }
  }
};

export default tramiteParametroService;